/**
 * The only module that fetches the clock's recordings.
 *
 * Two files of about seven kilobytes each, cut from the delivery by
 * `scripts/build-sound-assets.js` and served as static assets beside the
 * catalogue. They are fetched rather than bundled for the reason the catalogue
 * is: a base64 tick inside `main.js` is bytes on the critical path for a sound
 * most readers never reach, where a fetched one costs nothing until the first
 * round runs down to ten seconds.
 *
 * Answers RAW BYTES and nothing else. Turning them into something playable is
 * `decodeAudioData`, which is a browser API rather than a network one and lives
 * in `application/game/duelTick.js` — the same seam `carContentClient` draws
 * between fetching the catalogue and knowing what a car is.
 *
 * The timeout and the single retry are the repo's rule for anything outbound,
 * and they matter less here than anywhere else in the product: a tick that never
 * arrives is a silent clock, which is a state this app is built to have.
 */

const SOUND_BASE = '/assets/sound';
const REQUEST_TIMEOUT_MS = 10000;

async function fetchBytesOnce(url, { fetchImpl = globalThis.fetch, timeoutMs = REQUEST_TIMEOUT_MS } = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    try {
        const response = await fetchImpl(url, { signal: controller.signal });

        if (!response.ok) {
            throw new Error(`${url} responded ${response.status}`);
        }

        return await response.arrayBuffer();
    } finally {
        clearTimeout(timer);
    }
}

/** The bytes of one recording, by file name. Rejects like any other fetch. */
export async function fetchSound(name, options = {}) {
    const url = `${SOUND_BASE}/${name}`;

    try {
        return await fetchBytesOnce(url, options);
    } catch (error) {
        // Sequential and written out rather than looped, for the reason
        // `carContentClient` gives: a retry only means anything once the first
        // attempt has actually failed.
        return fetchBytesOnce(url, options);
    }
}
